/* Keeps the mobile scroll-snap gallery and the thumbnail strip in step.

   Broadcast's two mobile galleries are exclusive: the fader ("Thumbnails")
   drives `data-active-media` and so keeps the thumbs in sync, while the native
   slider scrolls but tells nobody. This element gives the slider the same
   feedback loop — scrolling reports the visible slide, and a thumb tap scrolls
   the slider — by reusing the theme's own `theme:media:select` event, so
   `product-thumbs` needs no changes. */
(() => {
  if (customElements.get('xali-gallery-sync')) return;

  const selectors = {
    images: 'product-images',
    list: '[data-product-media-list]',
    slide: '[data-media-id]',
  };

  const attributes = {
    activeMedia: 'data-active-media',
    mediaId: 'data-media-id',
  };

  // Ignore sub-pixel scroll noise, but stay well under one slide width.
  const SCROLL_EPSILON = 8;

  customElements.define(
    'xali-gallery-sync',
    class XaliGallerySync extends HTMLElement {
      constructor() {
        super();

        this.images = this.closest(selectors.images);
        this.list = this.images?.querySelector(selectors.list);
        this.slides = this.list ? Array.from(this.list.querySelectorAll(selectors.slide)) : [];
        this.listening = false;
        this.lastScroll = null;
        this.echo = false;

        this.mobile = window.matchMedia('(max-width: 749px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onScroll = () => this.reportVisibleSlide();
        this.onSelect = (event) => this.scrollToMedia(event.detail?.id);
        this.onBreakpoint = () => this.toggleListener();
      }

      connectedCallback() {
        if (!this.list || this.slides.length < 2) return;

        this.images.addEventListener('theme:media:select', this.onSelect);
        this.mobile.addEventListener('change', this.onBreakpoint);
        this.toggleListener();
      }

      disconnectedCallback() {
        this.images?.removeEventListener('theme:media:select', this.onSelect);
        this.mobile.removeEventListener('change', this.onBreakpoint);
        this.stopListening();
      }

      toggleListener() {
        this.mobile.matches ? this.startListening() : this.stopListening();
      }

      startListening() {
        if (this.listening) return;

        this.list.addEventListener('scroll', this.onScroll, {passive: true});
        this.listening = true;
      }

      stopListening() {
        if (!this.listening) return;

        this.list.removeEventListener('scroll', this.onScroll);
        this.listening = false;
        this.lastScroll = null;
      }

      reportVisibleSlide() {
        const scrollLeft = this.list.scrollLeft;

        if (this.lastScroll !== null && Math.abs(scrollLeft - this.lastScroll) < SCROLL_EPSILON) return;
        this.lastScroll = scrollLeft;

        const slide = this.getCentredSlide();
        const id = slide?.getAttribute(attributes.mediaId);
        if (!id || id === this.images.getAttribute(attributes.activeMedia)) return;

        // The theme answers this by setting data-active-media, which is what
        // product-thumbs watches. The flag stops our own listener treating it
        // as a thumb tap and scrolling the slider out from under the finger;
        // dispatch is synchronous, so it is spent by the time this returns.
        this.echo = true;
        this.images.dispatchEvent(new CustomEvent('theme:media:select', {detail: {id: id}}));
        this.echo = false;
      }

      // Nearest slide centre rather than a visibility threshold, so a partial
      // swipe resolves to one slide and unequal slide widths still work.
      getCentredSlide() {
        const bounds = this.list.getBoundingClientRect();
        const centre = bounds.left + bounds.width / 2;
        let winner = null;
        let shortest = Infinity;

        this.slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - centre);

          if (distance < shortest) {
            shortest = distance;
            winner = slide;
          }
        });

        return winner;
      }

      scrollToMedia(id) {
        if (this.echo || !this.mobile.matches || !id) return;

        const slide = this.slides.find((item) => item.getAttribute(attributes.mediaId) === id);

        slide?.scrollIntoView({
          inline: 'start',
          block: 'nearest',
          behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
        });
      }
    }
  );
})();
